import { Stripe } from '@stripe/stripe-js';
import { IconNames } from '../common-components/component-library/Icon';
import {
  CardBrand,
  CardName,
  PaymentMethod,
  PaymentMethodName,
  WalletName,
  WalletType,
} from './api/paymentRequests';

class CardValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CardValidationError';
  }
}

class InvalidCardError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidCardError';
  }
}

export async function handlePaymentSetupIntent({
  stripe,
  status,
  setupIntentClientSecret,
}: {
  stripe: Stripe;
  status: string;
  setupIntentClientSecret: string;
}) {
  if (status == 'requires_action') {
    const cardSetupResponse = await stripe.confirmCardSetup(setupIntentClientSecret);
    if (cardSetupResponse.error) {
      throw new CardValidationError(cardSetupResponse.error.message ?? 'Card validation failed');
    }
  } else if (status == 'requires_payment_method') {
    const errorMessage = 'The card was not approved, please try another payment option.';
    throw new InvalidCardError(errorMessage);
  }
}

type CardIconAndProviderName = {
  iconName: IconNames;
  providerName: PaymentMethodName | null;
};
export function getCardIconAndProviderName(
  paymentMethod?: PaymentMethod | null,
): CardIconAndProviderName {
  const result: CardIconAndProviderName = {
    iconName: 'credit-card',
    providerName: null,
  };

  if (!paymentMethod) {
    return result;
  }

  const { card } = paymentMethod;
  if (card?.wallet) {
    result.iconName = (
      card.wallet.type === WalletType.ApplePay ? 'apple-pay' : card.wallet.type
    ) as IconNames;
    result.providerName = WalletName[card.wallet.type];
  } else {
    const getCardIconName = (brand?: CardBrand): IconNames => {
      switch (brand) {
        case CardBrand.AmericanExpress:
          return 'american-express';
        default:
          return brand ?? 'credit-card';
      }
    };
    result.iconName = getCardIconName(card?.brand);
    result.providerName = card ? CardName[card.brand] : null;
  }

  result.iconName = result.iconName ?? 'credit-card';

  return result;
}
